import { useMemo, useState } from "react";
import { Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionsContext";
import {
  useOrdersDecoprof,
  useUpdateOrdersDecoprofStatus,
  type OrdersDecoprofRow,
} from "@/hooks/useOrdersDecoprof";
import {
  ORDER_DECOPROF_TYPE_OPTIONS,
  isMobileDevice,
  openPackingSlip,
  type OrderDecoprofType,
} from "@/lib/ordersDecoprof";
import OrdersDecoprofMultiSelect from "./OrdersDecoprofMultiSelect";

const OrdersDecoprofTab = () => {
  const { data: orders, isLoading, error } = useOrdersDecoprof();
  const updateStatus = useUpdateOrdersDecoprofStatus();
  const { hasPermission } = usePermissions();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<OrderDecoprofType[]>([]);
  const [supplierFilter, setSupplierFilter] = useState<string[]>([]);

  const allSuppliers = useMemo(() => {
    const set = new Set<string>();
    (orders ?? []).forEach((o) => o.primarySupplier && set.add(o.primarySupplier));
    return Array.from(set).sort();
  }, [orders]);

  const filteredOrders = useMemo(() => {
    let list = orders ?? [];
    if (typeFilter.length > 0) {
      list = list.filter((o) => typeFilter.includes(o.type));
    }
    if (supplierFilter.length > 0) {
      list = list.filter(
        (o) => o.primarySupplier && supplierFilter.includes(o.primarySupplier),
      );
    }
    return list;
  }, [orders, typeFilter, supplierFilter]);

  const toggleRow = (ordernumber: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      return next;
    });
  };

  const onlyOneSelected = selected.size === 1;
  const selectedOrdernumber = onlyOneSelected ? Array.from(selected)[0] : null;
  const selectedOrder = selectedOrdernumber
    ? (orders ?? []).find((o) => o.ordernumber === selectedOrdernumber) ?? null
    : null;
  const selectedOrderid = selectedOrder?.orderid ?? null;
  const canPrintPackingSlip = hasPermission("ordersDecoprof", "read");
  const printDisabled = !canPrintPackingSlip || !onlyOneSelected;
  const printDisabledReason = !canPrintPackingSlip
    ? "Geen toegang tot Orders Decoprof"
    : !onlyOneSelected
    ? "Selecteer precies 1 order"
    : "";

  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = async () => {
    if (!selectedOrdernumber || !selectedOrderid) return;

    // SYNC vóór await: open tab op desktop zodat iOS/Safari het niet blokkeert.
    // Op mobiel gebruiken we native download via <a download>, dus geen pre-open.
    let preOpenedWindow: Window | null = null;
    if (!isMobileDevice()) {
      preOpenedWindow = window.open("about:blank", "_blank");
      if (!preOpenedWindow) {
        toast.error("Pop-up geblokkeerd. Sta pop-ups toe voor dit domein.");
        return;
      }
    }

    setIsPrinting(true);
    try {
      await openPackingSlip(selectedOrderid, preOpenedWindow);
      await updateStatus.mutateAsync({
        ordernumber: selectedOrdernumber,
        status: "Uitgeprint",
      });
      toast.success(`Order ${selectedOrdernumber} gemarkeerd als Uitgeprint`);
    } catch (e: any) {
      if (preOpenedWindow && !preOpenedWindow.closed) {
        try { preOpenedWindow.close(); } catch { /* ignore */ }
      }
      toast.error(`Pakbon afdrukken mislukt: ${e.message ?? e}`);
    } finally {
      setIsPrinting(false);
    }
  };

  const renderStatusBadge = (status: OrdersDecoprofRow["status"]) => {
    const variant =
      status === "Aangemaakt"
        ? "default"
        : status === "Uitgeprint"
          ? "secondary"
          : "outline";
    return <Badge variant={variant}>{status}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <OrdersDecoprofMultiSelect
          label="Type order"
          options={ORDER_DECOPROF_TYPE_OPTIONS as readonly string[]}
          values={typeFilter}
          onChange={(v) => setTypeFilter(v as OrderDecoprofType[])}
        />
        <OrdersDecoprofMultiSelect
          label="Leverancier"
          options={allSuppliers}
          values={supplierFilter}
          onChange={setSupplierFilter}
        />

        <div className="ml-auto">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={handlePrint}
                    disabled={printDisabled || updateStatus.isPending || isPrinting}
                    className="gap-2"
                  >
                    {isPrinting || updateStatus.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Printer className="h-4 w-4" />
                    )}
                    Pakbon afdrukken
                  </Button>
                </span>
              </TooltipTrigger>
              {printDisabledReason && (
                <TooltipContent>{printDisabledReason}</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Resultaten-aantal */}
      {!isLoading && (
        <p className="text-sm text-muted-foreground">
          {filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""} gevonden
        </p>
      )}

      {/* Tabel */}
      <div className="border rounded-lg overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead>Ordernummer</TableHead>
              <TableHead>Type order</TableHead>
              <TableHead>Leverancier</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                  Laden...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-destructive">
                  Fout bij laden van orders
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Geen Decoprof-orders gevonden
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => (
                <TableRow key={order.ordernumber}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(order.ordernumber)}
                      onCheckedChange={() => toggleRow(order.ordernumber)}
                      aria-label={`Selecteer order ${order.ordernumber}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{order.ordernumber}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{order.type}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {order.primarySupplier ?? "Onbekend"}
                  </TableCell>
                  <TableCell>{renderStatusBadge(order.status)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default OrdersDecoprofTab;
